/**
 * First-run enrolment (portal-spec §4.1). Explains the zero-knowledge model,
 * takes a passphrase (with a light strength gate), then triggers enrol — which
 * generates the UMK + recovery code client-side and reveals the code once via
 * the RecoveryCodeModal.
 */
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useSession } from '../store/session';
import { isTooWeak, strength } from '../lib/passphrase';
import { Button, Card, ErrorNote, Field, Input } from '../components/ui';

export function Welcome() {
  const enrol = useSession((s) => s.enrol);
  const busy = useSession((s) => s.busy);
  const serverError = useSession((s) => s.error);
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const st = strength(pw);
  const tooWeak = isTooWeak(pw);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    setLocalError(null);
    if (tooWeak) {
      setLocalError('Choose a longer passphrase — at least 12 characters, with some variety.');
      return;
    }
    if (pw !== confirm) {
      setLocalError('Passphrases do not match.');
      return;
    }
    void enrol(pw);
  };

  return (
    <div className="grid min-h-full place-items-center p-4">
      <Card className="w-full max-w-md space-y-5">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-text">Welcome to Heivol Locker</h1>
          <p className="text-sm text-text-secondary">
            Your passphrase encrypts everything. We can never see or reset it. Choose something
            strong and memorable — you will set up a recovery code next.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Vault passphrase" hint="Different from your heivol-id password.">
            <Input
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
            />
          </Field>

          {pw.length > 0 && (
            <div className="space-y-1">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
                <div
                  className={`h-full transition-all ${st.score >= 3 ? 'bg-success' : st.score >= 2 ? 'bg-warning' : 'bg-error'}`}
                  style={{ width: `${Math.min(100, (st.score / 5) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-text-secondary">Strength: {st.label}</p>
            </div>
          )}

          <Field label="Confirm passphrase">
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>

          {localError !== null && <ErrorNote>{localError}</ErrorNote>}
          {serverError !== null && <ErrorNote>{serverError}</ErrorNote>}

          <Button type="submit" disabled={busy} className="w-full">
            {busy ? 'Creating your vault…' : 'Create my vault'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
